import React, { useState } from 'react'
import { View } from 'react-native'
import { Header, Screen, SearchBar, FeatureTooltip } from '@components'
import { useRoute, useTheme } from '@react-navigation/native'
import { getCommonStyles, Scope } from '@utils'
import { useLanguage } from '@context'
import { ContactsList, ContactFilters, AddContactOptionsModal } from './components'
import { useListingContacts } from './hooks'
import { getListingContactsStyles } from './styles'
import { ScreenTabContactProps } from '../../../navigation/navigator/TabBar/TabContact'
import { Contact, useInitiateIndividualConversation, useScreen } from '@hooks'
import { ListingContactsFilters } from './ListingContactsFilters'

export const ListingContacts: React.FC<ScreenTabContactProps<'ListContacts'>> = ({navigation}) => {
  const { t } = useLanguage()
  const theme = useTheme()
  const route = useRoute()
  const {isMobile, isDesktop} = useScreen()
  const styles = getListingContactsStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const [isAddContactModalVisible, setIsAddContactModalVisible] = useState(false)
    
  const { mutateAsync: initiateIndividualConversation } = useInitiateIndividualConversation()

  // Hook principal du système
  const {
    contacts,
    searchText,
    scope,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    setSearchText,
    setScope,
    handleFilterPress,
    hasActiveFilters,
    getActiveFiltersCount
  } = useListingContacts(navigation)

  const emptyMessage = hasActiveFilters()
    ? t('contactsList.noContactFound')
    : scope.includes(Scope.PRIVATE)
      ? t('contact.emptyContactPrivate')
      : scope.includes(Scope.PROFESSIONAL)
        ? t('contact.emptyContactPro')
        : t('contactsList.noContactFound')

  const handleContactPress = (contact: Contact) => {
    (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contact.id }
    })
  }

  const sendMessage = async (contact: Contact) => {
    const conversation = await initiateIndividualConversation({
      participantAccountId: contact.accountId,
    });
    
    (navigation as any).navigate('Messages', {
      screen: 'Conversation',
      params: {
        conversationId: conversation.id,
      }
    })
  }
  
  return (
    <Screen noPaddingBottom>
      <View style={!isMobile? styles.containerList : commonStyles.flex}>
        <View style={[commonStyles.flex, commonStyles.fullHeight]}>
          <Header
            title={t('contact.title')}
            iconRight="userPlus"
            onPressRight={() => {
              setIsAddContactModalVisible(true)
            }}
          />

          <FeatureTooltip
            id="listing_contacts"
            title={t('featureTooltip.listing_contacts.title')}
            description={t('featureTooltip.listing_contacts.description')}
            icon="users"
          />

          <View style={isDesktop && styles.containerSearch}>
            <View style={isDesktop ? styles.containerSearchBar : [styles.container, commonStyles.paddingBottom]}>
              <SearchBar
                value={searchText}
                onChangeText={setSearchText}
                placeholder={t('contact.searchPlaceholder')}
              />      
            </View>

            <ContactFilters
              scope={scope}
              onScopeChange={setScope}
              onFilterPress={isMobile? handleFilterPress : () => {}}
              activeFiltersCount={getActiveFiltersCount()}
              hasActiveFilters={hasActiveFilters()}
            />
          </View>
          <ContactsList
            contacts={contacts || []}
            emptyMessage={emptyMessage}
            onContactPress={handleContactPress}
            onMessageContact={sendMessage}
            isLoading={isPending}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        </View>
        {!isMobile && <ListingContactsFilters navigation={navigation as any} route={route as any} />}
      </View>

      <AddContactOptionsModal
        isVisible={isAddContactModalVisible}
        setIsVisible={setIsAddContactModalVisible}
      />
    </Screen>
  )
} 